import React, { useRef, useState } from 'react';

import { useClickAway } from 'react-use';
import { Squash as Hamburger } from 'hamburger-react';
import { AnimatePresence, motion } from 'framer-motion';
import { routes } from '../../routes/routes';
import {
  NavMobileContainer,
  NavLink,
  StyledList,
  SubMenuContainer,
  SubMenuItem,
} from './NavMobile.styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { Logo } from './Logo';
import { useTranslation } from 'react-i18next';

interface NavMobileProps {
  isOpen: boolean;
  setOpen: (value: React.SetStateAction<boolean>) => void;
}

export const NavMobile: React.FC<NavMobileProps> = ({ isOpen, setOpen }) => {
  const ref = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showHikingSubmenu, setShowHikingSubmenu] = useState(false);

  useClickAway(ref, () => setOpen(false));

  const isHomePage = location.pathname === '/';

  const scrollToSection = (sectionId: string) => {
    // Najpierw przejdź do strony wypraw jeśli nie jesteśmy na niej
    if (location.pathname !== '/projects/hiking') {
      navigate('/projects/hiking');
      // Poczekaj na załadowanie strony przed scrollowaniem
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Jeśli już jesteśmy na stronie, scrolluj od razu
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setOpen(false);
    setShowHikingSubmenu(false);
  };

  return (
    <NavMobileContainer ref={ref}>
      {!isHomePage && <Logo isVisible={!isOpen} />}
      <Hamburger toggled={isOpen} size={20} toggle={() => setOpen((prev) => !prev)} />
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0.1 }}
            transition={{
              duration: 0.1,
            }}
          >
            <StyledList>
              {routes
                .filter((route) => route.showInNav)
                .map((route, idx) => {
                  const { href } = route;
                  const isActive = location.pathname === href;

                  // Specjalne traktowanie dla zakładki "Wyprawy"
                  if (route.title === 'nav.hiking') {
                    return (
                      <motion.li
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{
                          type: 'spring',
                          stiffness: 280,
                          damping: 20,
                          delay: 0.1 + idx / 15,
                        }}
                        key={route.title}
                      >
                        <NavLink
                          onClick={() => {
                            if (showHikingSubmenu) {
                              setShowHikingSubmenu(false);
                            } else {
                              navigate(route.href);
                              setOpen(false);
                            }
                          }}
                          href={route.href}
                          isActive={isActive}
                        >
                          <span>{t(route.title)}</span>
                        </NavLink>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setShowHikingSubmenu(!showHikingSubmenu);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'black',
                            fontSize: '1.2em',
                            marginLeft: '10px',
                            cursor: 'pointer',
                          }}
                        >
                          {showHikingSubmenu ? '−' : '+'}
                        </button>
                        {showHikingSubmenu && (
                          <SubMenuContainer>
                            <SubMenuItem onClick={() => scrollToSection('wyjazdy-2026')}>
                              {t('nav.year2026')}
                            </SubMenuItem>
                            <SubMenuItem onClick={() => scrollToSection('archiwalne')}>
                              {t('nav.archived')}
                            </SubMenuItem>
                          </SubMenuContainer>
                        )}
                      </motion.li>
                    );
                  }

                  return (
                    <motion.li
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{
                        type: 'spring',
                        stiffness: 280,
                        damping: 20,
                        delay: 0.1 + idx / 15,
                      }}
                      key={route.title}
                    >
                      <NavLink
                        onClick={() => setOpen((prev) => !prev)}
                        href={route.href}
                        isActive={isActive}
                      >
                        <span>{t(route.title)}</span>
                      </NavLink>
                    </motion.li>
                  );
                })}
            </StyledList>
          </motion.div>
        )}
      </AnimatePresence>
    </NavMobileContainer>
  );
};
